// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import axios, { AxiosError } from 'axios';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useEnv } from '../../context/EnvironmentContext/EnvironmentContext';
import { ImpersonatedUserId } from '../../constants/impersonateUser';
import { SIGN_IN_CALLBACK_RETURN_URL } from '../../constants/auth';
import { Loading } from '@wfp/ui';
import { ErrorResponse } from '@partnerconnect-monorepo/types/ErrorResponse';
import { removeLocalStorageImpersonationData } from '../../utils/impersonationStorage';
import { replaceUrl } from '../../utils/navigation';

const LANDING_PAGE_PATHNAME = '/';

const instance = axios.create();

const AxiosInterceptor = ({ children }: PropsWithChildren) => {
  const { baseUrl } = useEnv();

  const { user, removeUser, isLoading, error, isAuthenticated, events } =
    useAuth();

  const removeUserAndRedirectToHomepage = useCallback(() => {
    removeUser();
    removeLocalStorageImpersonationData();

    if (
      window.location.pathname !== LANDING_PAGE_PATHNAME &&
      !window.location.href.includes(SIGN_IN_CALLBACK_RETURN_URL)
    ) {
      replaceUrl(`/?${SIGN_IN_CALLBACK_RETURN_URL}=${window.location.href}`);
    }
  }, [removeUser]);

  const isAccessingPublicPage = [LANDING_PAGE_PATHNAME, '/support'].includes(
    window.location.pathname
  );

  useLayoutEffect(() => {
    if ((error || !isAuthenticated) && !isLoading && !isAccessingPublicPage) {
      removeUserAndRedirectToHomepage();
      return;
    } else if (isLoading && !user) return;

    const requestInterceptor = instance.interceptors.request.use(
      (config) => {
        config.baseURL = baseUrl;

        if (!config.headers) return config;

        if (user?.id_token) {
          config.headers['authorization'] = `Bearer ${user.id_token}`;
        }

        const impersonatedUserId = localStorage.getItem(ImpersonatedUserId);
        if (impersonatedUserId) {
          config.headers['X-Impersonate-User'] = impersonatedUserId;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ErrorResponse<{ code: string }>>) => {
        if (error.response || user?.expired) {
          if (error.response?.status === 401) {
            if (error.response.data.error?.details?.code === 'unknown_user') {
              return Promise.reject(error);
            }
            removeUserAndRedirectToHomepage();
            return Promise.reject(error);
          } else if (
            error.response?.status === 400 &&
            error.response.data.error?.details?.code === 'invalid_grant'
          ) {
            removeUserAndRedirectToHomepage();
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      instance.interceptors.request.eject(requestInterceptor);
      instance.interceptors.response.eject(responseInterceptor);
    };
  }, [
    baseUrl,
    user,
    isLoading,
    removeUserAndRedirectToHomepage,
    error,
    isAccessingPublicPage,
    isAuthenticated
  ]);

  useEffect(() => {
    if (!events) return;

    events.addSilentRenewError(removeUserAndRedirectToHomepage);

    return () => {
      events.removeSilentRenewError(removeUserAndRedirectToHomepage);
    };
  }, [events, removeUserAndRedirectToHomepage]);

  return !isLoading && (isAuthenticated || isAccessingPublicPage) ? (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  ) : (
    <Loading />
  );
};

export { AxiosInterceptor, instance as axios };
